import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import {
  Plus, GitBranch, Trash2, Edit2, ChevronLeft, Save, Upload, Mic,
  Phone, Users, List, Voicemail, PhoneOff, Globe, Megaphone, X,
} from 'lucide-react';

/* ─── Catálogo de acciones del menú (etiquetas en español) ───── */
const ACTIONS = [
  { value: '',            label: 'Sin acción',            icon: X },
  { value: 'extension',   label: 'Conectar a Extensión',  icon: Phone },
  { value: 'queue',       label: 'Enviar a Cola',         icon: List },
  { value: 'ringgroup',   label: 'Grupo de Timbrado',     icon: Users },
  { value: 'ivr',         label: 'Otro menú IVR',         icon: GitBranch },
  { value: 'voicemail',   label: 'Buzón de Voz',          icon: Voicemail },
  { value: 'external',    label: 'Número Externo',        icon: Globe },
  { value: 'announcement',label: 'Anuncio',               icon: Megaphone },
  { value: 'hangup',      label: 'Colgar',                icon: PhoneOff },
];
const ACTION_LABEL = Object.fromEntries(ACTIONS.map(a => [a.value, a.label]));

// Teclas del menú (como el marcador telefónico)
const KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];

const emptyForm = () => ({
  number: '', name: '', greeting: '', greetingFile: '',
  timeout: 10, maxAttempts: 3, language: 'es',
  invalidMessage: '', timeoutMessage: '', description: '',
  dids: [],   // números DID que entran directamente a este IVR
});

/* ─── Números DID asignados (chips) ───────────────────────────────
   Cada DID crea una ruta entrante hacia este IVR. Se escriben uno a uno
   y se confirman con Enter o coma. */
function DIDPicker({ value = [], onChange }) {
  const [input, setInput] = useState('');

  const add = () => {
    const clean = input.replace(/[^0-9+*#]/g, '').trim();
    if (!clean) return;
    if (value.includes(clean)) { toast.error('Ese DID ya está asignado'); setInput(''); return; }
    onChange([...value, clean]);
    setInput('');
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.length === 0 && (
          <span className="text-xs text-muted">Sin DID asignado — solo se podrá llamar por la extensión virtual</span>
        )}
        {value.map(d => (
          <span key={d} className="inline-flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded px-2 py-1">
            <span className="text-xs font-mono text-gray-200">{d}</span>
            <button type="button" onClick={() => onChange(value.filter(x => x !== d))}
              className="text-muted hover:text-red-400" title="Quitar DID">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <label htmlFor="ivr-did-input" className="sr-only">Agregar DID</label>
        <input id="ivr-did-input" className="input flex-1" value={input} placeholder="Ej. 17867558329"
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
          }} />
        <button type="button" onClick={add} className="btn-secondary px-3" title="Añadir DID">
          <Plus size={15} />
        </button>
      </div>
    </div>
  );
}

/* ─── Selector de destino según la acción ────────────────────── */
function DestinationPicker({ type, value, onChange, extensions, queues, ringGroups, ivrs, currentIvrId }) {
  if (!type || type === 'hangup') {
    return <span className="text-xs text-muted">—</span>;
  }
  if (type === 'external' || type === 'announcement') {
    return (
      <input
        className="input py-1.5 text-sm w-full"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={type === 'external' ? 'Ej. 3001234567' : 'Nombre del audio'}
      />
    );
  }
  const opts =
    type === 'extension' || type === 'voicemail'
      ? (extensions || []).map(e => ({ v: e.number, l: `${e.number}  ${e.caller_id_name || ''}`.trim() }))
    : type === 'queue'
      ? (queues || []).map(q => ({ v: q.number, l: `${q.number}  ${q.name || ''}`.trim() }))
    : type === 'ringgroup'
      ? (ringGroups || []).map(g => ({ v: g.number, l: `${g.number}  ${g.name || ''}`.trim() }))
    : type === 'ivr'
      ? (ivrs || []).filter(i => i.id !== currentIvrId).map(i => ({ v: i.number, l: `${i.number}  ${i.name || ''}`.trim() }))
      : [];

  return (
    <select aria-label="Destino" className="input py-1.5 text-sm w-full" value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">— Seleccionar —</option>
      {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

/* ─── Editor de IVR ──────────────────────────────────────────── */
function IVREditor({ ivrId, onBack }) {
  const qc = useQueryClient();
  const isNew = !ivrId;
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState(emptyForm());
  const [menu, setMenu] = useState({});   // { '1': { type, destination, description } }
  // Audio elegido en un IVR NUEVO: aún no hay id contra el que subirlo, así que
  // se guarda aquí y se envía automáticamente al crearse el IVR.
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);
  const loadedFor = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setKey = (digit, patch) =>
    setMenu(m => ({ ...m, [digit]: { ...(m[digit] || { type: '', destination: '' }), ...patch } }));

  // Datos para poblar los destinos
  const { data: extensions = [] } = useQuery('ivr-exts', () => api.get('/extensions?limit=200').then(r => r.data?.data || r.data || []));
  const { data: queues = [] } = useQuery('ivr-queues',
    () => api.get('/queues').then(r => r.data?.data || r.data || []).catch(() => []));
  const { data: ringGroups = [] } = useQuery('ivr-rgs',
    () => api.get('/ringgroups').then(r => r.data?.data || r.data || []).catch(() => []));
  const { data: ivrs = [] } = useQuery('ivr', () => api.get('/ivr').then(r => r.data));

  const { data: detail } = useQuery(
    ['ivr-detail', ivrId],
    () => api.get('/ivr/' + ivrId).then(r => r.data),
    { enabled: !isNew, refetchOnWindowFocus: false }
  );

  // Poblar el formulario (desde caché o red) una sola vez por IVR
  useEffect(() => {
    if (isNew || !detail) return;
    if (loadedFor.current === ivrId) return;
    loadedFor.current = ivrId;
    setForm({
      number: detail.number || '',
      name: detail.name || '',
      greeting: detail.greeting || '',
      greetingFile: detail.greeting_file || '',
      timeout: detail.timeout ?? 10,
      maxAttempts: detail.max_attempts ?? 3,
      language: detail.language || 'es',
      invalidMessage: detail.invalid_message || '',
      timeoutMessage: detail.timeout_message || '',
      description: detail.description || '',
      dids: detail.dids || [],
    });
    const m = {};
    (detail.options || []).forEach(o => {
      m[o.digit] = { type: o.type || '', destination: o.destination || '', description: o.description || '' };
    });
    setMenu(m);
  }, [detail, ivrId, isNew]);

  const saveMut = useMutation(
    (payload) => isNew ? api.post('/ivr', payload).then(r => r.data) : api.put('/ivr/' + ivrId, payload).then(r => r.data),
    {
      onSuccess: async (d) => {
        qc.invalidateQueries('ivr');
        if (!isNew) qc.invalidateQueries(['ivr-detail', ivrId]);
        // Si se eligió un audio antes de guardar (IVR nuevo), subirlo ahora que
        // ya existe el id. Se espera a que termine para no volver atrás antes.
        if (isNew && pendingFile && d?.id) {
          try {
            await uploadMut.mutateAsync({ file: pendingFile, id: d.id });
            setPendingFile(null);
          } catch (_) { /* uploadMut ya avisa del error; el IVR sí quedó creado */ }
        }
        toast.success(isNew ? 'IVR creado' : 'IVR guardado');
        if (isNew) onBack();
      },
      onError: (e) => toast.error(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || 'Error al guardar'),
    }
  );

  // El id se pasa explícito: al crear un IVR el audio se sube justo después,
  // cuando `ivrId` todavía es null pero ya tenemos el id que devolvió el POST.
  // OJO: hay que forzar 'multipart/form-data'. La instancia de axios lleva
  // 'application/json' por defecto y, si no se anula, el FormData viaja como
  // JSON: multer no lo parsea y el backend responde "Archivo requerido".
  // Axios detecta el FormData y completa el boundary correcto.
  const uploadMut = useMutation(
    ({ file, id }) => {
      const fd = new FormData();
      fd.append('audio', file);
      return api.post(`/ivr/${id || ivrId}/upload-greeting`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(r => r.data);
    },
    {
      onSuccess: (d) => { set('greetingFile', d.greetingFile); toast.success('Audio subido'); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error al subir el audio'),
    }
  );

  const onSave = () => {
    if (!form.number || !form.name) return toast.error('Número y nombre son obligatorios');
    const options = Object.entries(menu)
      .filter(([, v]) => v.type)                                  // solo teclas con acción
      .filter(([, v]) => v.type === 'hangup' || v.destination)    // destino requerido salvo colgar
      .map(([digit, v]) => ({ digit, type: v.type, destination: v.destination || '', description: v.description || '' }));
    saveMut.mutate({ ...form, options });
  };

  const configured = Object.values(menu).filter(v => v.type).length;

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} aria-label="Volver" className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white truncate">
            {isNew ? 'Nuevo IVR' : `Editar IVR ${form.number}`}
            {form.name && <span className="text-gray-400 font-normal"> · {form.name}</span>}
          </h1>
          <p className="text-xs text-muted">{configured} de {KEYS.length} teclas configuradas</p>
        </div>
        <button onClick={onSave} disabled={saveMut.isLoading} className="btn-primary">
          <Save size={16} /> {saveMut.isLoading ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      {/* Pestañas */}
      <div className="flex gap-1 border-b border-gray-800">
        {[{ id: 'general', label: 'General' }, { id: 'options', label: 'Opciones' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-blue-500 text-white' : 'border-transparent text-muted hover:text-gray-300'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="space-y-5">
          {/* Datos generales */}
          <div className="card space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="ivr-number">Número de extensión virtual *</label>
                <input id="ivr-number" className="input" value={form.number} disabled={!isNew}
                  onChange={e => set('number', e.target.value.replace(/\D/g, ''))} placeholder="800" />
                <p className="text-xs text-muted mt-1">
                  {isNew ? 'Rango sugerido 800-899. No se puede cambiar después.' : 'No editable'}
                </p>
              </div>
              <div>
                {/* span, no label: DIDPicker ya trae su propia etiqueta accesible interna */}
                <span className="label">Número(s) DID asignado(s)</span>
                <DIDPicker value={form.dids} onChange={v => set('dids', v)} />
                <p className="text-xs text-muted mt-1">
                  Las llamadas entrantes a estos números abrirán este IVR.
                </p>
              </div>
              <div>
                <label className="label" htmlFor="ivr-name">Nombre del IVR *</label>
                <input id="ivr-name" className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Menú Principal" />
              </div>
              <div>
                <label className="label" htmlFor="ivr-language">Idioma de los mensajes</label>
                <select id="ivr-language" className="input" value={form.language} onChange={e => set('language', e.target.value)}>
                  <option value="es">Español</option>
                  <option value="en">Inglés</option>
                  <option value="pt">Portugués</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="ivr-timeout">Espera (seg)</label>
                  <input id="ivr-timeout" type="number" min={3} max={60} className="input" value={form.timeout}
                    onChange={e => set('timeout', parseInt(e.target.value, 10) || 10)} />
                </div>
                <div>
                  <label className="label" htmlFor="ivr-max-attempts">Máx. intentos</label>
                  <input id="ivr-max-attempts" type="number" min={1} max={5} className="input" value={form.maxAttempts}
                    onChange={e => set('maxAttempts', parseInt(e.target.value, 10) || 3)} />
                </div>
              </div>
            </div>
          </div>

          {/* Mensaje de bienvenida */}
          <div className="card space-y-3">
            <h3 className="text-sm font-semibold text-white">Mensaje de bienvenida</h3>

            <div>
              <label className="label" htmlFor="ivr-greeting-file">Audio (recomendado)</label>
              <div className="flex gap-2">
                <input id="ivr-greeting-file" className="input flex-1" readOnly
                  value={form.greetingFile || (pendingFile ? pendingFile.name : '')}
                  placeholder="Sin audio — se usará el texto TTS" />
                {(form.greetingFile || pendingFile) && (
                  <button type="button"
                    onClick={() => { set('greetingFile', ''); setPendingFile(null); }}
                    className="btn-secondary px-3" title="Quitar audio"><Trash2 size={15} /></button>
                )}
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="btn-secondary px-3" title="Subir audio">
                  <Upload size={15} />
                </button>
                <input ref={fileRef} type="file" accept="audio/*,.wav" className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) {
                      // En un IVR nuevo aún no hay id: se guarda y se sube al crearlo.
                      if (isNew) setPendingFile(f);
                      else uploadMut.mutate({ file: f });
                    }
                    e.target.value = '';
                  }} />
              </div>
              <p className="text-xs text-muted mt-1">
                Formato WAV (PCM, 8 kHz, 16 bits, Mono).
                {isNew && pendingFile && <span className="text-yellow-500"> Se subirá al guardar el IVR.</span>}
                {uploadMut.isLoading && <span className="text-blue-400"> Subiendo…</span>}
              </p>
            </div>

            <div>
              <label className="label" htmlFor="ivr-greeting-text">Texto (TTS) — se usa si no hay audio</label>
              <textarea id="ivr-greeting-text" className="input h-20 resize-none" value={form.greeting}
                onChange={e => set('greeting', e.target.value)}
                placeholder="Bienvenido. Marque 1 para ventas, 2 para soporte…" />
            </div>
          </div>
        </div>
      )}

      {tab === 'options' && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Menú de opciones</h3>
            <span className="text-xs text-muted">Elige la acción y el destino de cada tecla</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="table-th w-20">Tecla</th>
                  <th className="table-th">Acción</th>
                  <th className="table-th">Destino</th>
                </tr>
              </thead>
              <tbody>
                {KEYS.map(k => {
                  const row = menu[k] || { type: '', destination: '' };
                  const active = !!row.type;
                  return (
                    <tr key={k} className={`border-b border-gray-800/60 ${active ? '' : 'opacity-70'}`}>
                      <td className="px-3 py-2">
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center font-mono font-bold text-sm ${
                          active ? 'bg-blue-600/20 text-blue-300 border border-blue-700/40' : 'bg-gray-800 text-muted'
                        }`}>{k}</span>
                      </td>
                      <td className="px-3 py-2">
                        <select aria-label={`Acción para la tecla ${k}`} className="input py-1.5 text-sm w-full" value={row.type}
                          onChange={e => setKey(k, { type: e.target.value, destination: '' })}>
                          {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <DestinationPicker
                          type={row.type} value={row.destination}
                          onChange={v => setKey(k, { destination: v })}
                          extensions={extensions} queues={queues} ringGroups={ringGroups}
                          ivrs={ivrs} currentIvrId={ivrId}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 pt-3 border-t border-gray-800 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="ivr-invalid-message">Mensaje si la opción es inválida</label>
              <input id="ivr-invalid-message" className="input" value={form.invalidMessage}
                onChange={e => set('invalidMessage', e.target.value)} placeholder="Opción no válida" />
            </div>
            <div>
              <label className="label" htmlFor="ivr-timeout-message">Mensaje si no marca nada</label>
              <input id="ivr-timeout-message" className="input" value={form.timeoutMessage}
                onChange={e => set('timeoutMessage', e.target.value)} placeholder="No se recibió ninguna opción" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Página principal (lista) ───────────────────────────────── */
export default function IVRPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [view, setView] = useState({ mode: 'list' }); // list | edit | new

  const { data = [], isLoading } = useQuery('ivr', () => api.get('/ivr').then(r => r.data));

  const delMut = useMutation((id) => api.delete('/ivr/' + id), {
    onSuccess: () => { qc.invalidateQueries('ivr'); toast.success('IVR eliminado'); },
    onError: (e) => toast.error(e.response?.data?.error || 'Error al eliminar'),
  });

  if (view.mode === 'edit' || view.mode === 'new') {
    return <IVREditor ivrId={view.mode === 'edit' ? view.id : null} onBack={() => setView({ mode: 'list' })} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">IVR / Recepcionista Digital</h1>
          <p className="text-gray-400 text-sm">{data.length} menús configurados</p>
        </div>
        <button onClick={() => setView({ mode: 'new' })} className="btn-primary">
          <Plus size={18} /> Nuevo IVR
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : !data.length ? (
        <div className="card text-center py-16">
          <GitBranch size={34} className="text-gray-700 mx-auto mb-3" />
          <p className="text-muted text-sm">Sin menús IVR configurados</p>
          <button onClick={() => setView({ mode: 'new' })} className="btn-primary mt-4 mx-auto">
            <Plus size={16} /> Crear el primero
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.map(ivr => (
            <div key={ivr.id} className="card hover:border-gray-700 transition-colors">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-600/20 rounded-xl flex items-center justify-center shrink-0">
                  <GitBranch size={18} className="text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">{ivr.name}</div>
                  <div className="text-xs text-muted">
                    Ext. <span className="font-mono">{ivr.number}</span> · espera {ivr.timeout}s
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setView({ mode: 'edit', id: ivr.id })}
                    className="p-1.5 text-muted hover:text-blue-400 rounded-lg" title="Editar">
                    <Edit2 size={15} />
                  </button>
                  <button onClick={async () => { if (await confirm({ title: `Eliminar el IVR ${ivr.number}`, message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true, checkCalls: true })) delMut.mutate(ivr.id); }}
                    className="p-1.5 text-muted hover:text-red-400 rounded-lg" title="Eliminar">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {ivr.options?.length > 0 ? (
                <div className="space-y-1">
                  {ivr.options.slice(0, 5).map(opt => (
                    <div key={opt.id} className="flex items-center gap-2 text-xs">
                      <span className="w-5 h-5 bg-gray-800 rounded flex items-center justify-center text-gray-300 font-mono font-bold shrink-0">
                        {opt.digit}
                      </span>
                      <span className="text-muted truncate">{ACTION_LABEL[opt.type] || opt.type}</span>
                      <span className="text-gray-300 font-mono ml-auto shrink-0">{opt.destination}</span>
                    </div>
                  ))}
                  {ivr.options.length > 5 && (
                    <p className="text-[11px] text-muted pt-1">+{ivr.options.length - 5} opciones más</p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted">Sin opciones configuradas</p>
              )}

              {(ivr.greeting_file || ivr.greeting) && (
                <div className="mt-3 pt-2 border-t border-gray-800 flex items-center gap-1.5 text-[11px] text-muted">
                  <Mic size={11} />
                  <span className="truncate">{ivr.greeting_file || 'Mensaje TTS'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
